import styled from "styled-components";
import { COLOR } from "../helpers/theme";
import Select, { StylesConfig } from "react-select";

type InputType = 'text' | 'date' | 'time' | 'datetime-local' | 'number' | 'password';

interface SelectProps {
  options: any[];
  value: any,
  onChange: (e: any) => void;
}

interface XrInputAvProps {
  /** input label */
  label?: string;
  /** input type, if you want to use advanced select, leave it empty */
  type?: InputType;
  /** if true, will display required asterix */
  required: boolean;
  /** placeholder when input is empty */
  placeholder?: string;
  /** if true, input will be disabled */
  disabled?: boolean;
  /** if true, input will be disabled but with loading indicator */
  isLoading?: boolean;
  /** if set, error state and message will be visible */
  errorMessage?: string[];
  /** required if you want to use input select */
  selectProps?: SelectProps;
  /** input value from state. For advanced select, use the one inside selectProps instead */
  value?: string | number | readonly string[] | undefined;
  /** triggered when you try to change input value. For advanced select, use the one inside selectProps instead. */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const XrInputAv = (props: XrInputAvProps) => {
  const hasErrors = () => {
    const errorMessages = props.errorMessage;
    if (typeof errorMessages === 'undefined') return false;
    if (errorMessages.length === 0) return false;
    return true;
  }
  const renderErrorMessage = () => {
    const errorMessages = props.errorMessage;
    if (typeof errorMessages === 'undefined') return;
    if (errorMessages.length === 0) return;
    
    return (
      <div className="input--errors">
        {(() => {
          if (errorMessages.length === 1) {
            return errorMessages[0];
          } else {
            return (
              <ul>
                {errorMessages.map((item, index) => {
                  return <li key={index}>{item}</li>
                })}
              </ul>
            );
          }
        })()}
      </div>
    );
  }

  const renderRequired = () => {
    if (!props.required) return;
    return (
      <span className="required">*</span>
    );
  };

  const isUsingAdvancedSelect = () => {
    if (typeof props.selectProps === 'undefined') return false;
    return true;
  }

  const renderInputType = () => {
    const selectProps = props.selectProps
    if (typeof selectProps === 'undefined') {
      return (
        <input
          type={props.type} 
          value={props.value}
          onChange={props.onChange}
          disabled={props.isLoading || props.disabled}
        />
      );
    }
    return (
      <Select
        styles={SelectStyle}
        isDisabled={props.isLoading || props.disabled}
        options={selectProps.options}
        value={selectProps.value}
        placeholder={props.placeholder}
        onChange={(e: any) => selectProps.onChange(e)}
        menuPortalTarget={document.querySelector("body")}
      />
    );
  }

  return (
    <XrInputAvWrapper hasError={hasErrors()} hasLabel={!!props.label}>
      <div className={`input--field ${isUsingAdvancedSelect() ? 'select' : ''}`}>
        { props.label ? ( <label>{props.label} {renderRequired()}</label> ) : null }
        {renderInputType()}
      </div>
      {renderErrorMessage()}
    </XrInputAvWrapper>
  );
}

interface XrInputAvWrapperProps {
  disabled?: boolean;
  hasError?: boolean;
  hasLabel: boolean;
}

const XrInputAvWrapper = styled.div<XrInputAvWrapperProps>`
  width: 100%;

  & > .input--field {
    z-index: 0;
    width: 100%;
    height: ${props => {
      if (props.hasLabel) return '65px';
      return '34px';
    }};
    padding: 10px;
    display: block;
    margin-top: -1px;
    padding-top: 10px;
    border-radius: 5px;
    position: relative;
    margin-bottom: ${props => {
      if (props.hasError) {
        return '5px';
      } else {
        return '0px';
      }
    }};
    background-color: ${props => {
      if (props.disabled) {
        return COLOR.INPUT_DISABLED;
      }
      else if (props.hasError) {
        return COLOR.DANGER_INPUT_BACKGROUND;
      } else {
        return '#fff';
      }
    }};
    border: 1px solid ${props => {
      if (props.hasError) {
        return COLOR.DANGER_BORDER;
      } else {
        return '#e2e2e2';
      }
    }};

    & > * {
      width: 100%;
      display: block;
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
    }

    & > input {
      background-color: inherit;
    }

    & > .react-datepicker-wrapper {
      & > .react-datepicker__input-container {
        & > input {
          width: 100%;
          border: none;
        }
      }
    }

    & > label {
      z-index: 1;
      display: flex;
      font-size: 14px;
      padding-left: 1px;
      position: relative;
      align-items: center;
      padding-bottom: 10px;
      color: ${props => {
        if (props.hasError) {
          return COLOR.DANGER_BORDER;
        } else {
          return COLOR.TEXT_DESCRIPTIVE;
        }
      }};

      & > .required {
        color: red;
        margin-left: 5px;
        font-weight: 900;
        font-size: 10px;
      }

      & .label--radio {
        padding-top: 4px;
        margin-right: 5px;

        & > input { 
          margin: 0;
        }
      }
    }

    & > *:last-child {
      position: absolute;
      z-index: 0;
      left: 0;
      top: 0%;
      border: none;
      font-size: 14px;
      padding-top: ${props => {
        if (props.hasLabel) return '33px';
        return '7px';
      }};
      padding-left: 11px;
      padding-right: 10px;
      padding-bottom: 7px;
    }

    &.select {
      & > *:last-child {
        padding: 0;
      }
    }

    &.no--label {
      height: 40px;
      & > input {
        padding-top: 8px;
      }
    }
  }

  & > .input--errors {
    font-size: 14px;
    line-height: 125%;
    margin-bottom: 15px;
    color: ${COLOR.DANGER};
    display: ${props => {
      if (props.hasError) {
        return 'block';
      } else {
        return 'none';
      }
    }};

    & > ul {
      margin: 0;
      padding-left: 15px;
      font-size: inherit;
      & > li {
        font-size: inherit;
      }
    }
  }
`;

const SelectStyle: StylesConfig<{}, false> = {
  input: (provided, state) => ({
    ...provided,
    margin: 0,
    fontSize: "14px",
    lineHeight: "14px",
    color: "#535353",
    fontWeight: "normal",
    padding: "30px 10px 20px 12px",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    fontSize: "14px",
    lineHeight: "14px",
    color: "#535353",
    fontWeight: "normal",
    padding: "37px 10px 20px 9px",
  }),
  placeholder: (provided, state) => ({
    ...provided,
    fontSize: "14px",
    lineHeight: "14px",
    color: "#8f8f8f",
    fontWeight: "normal",
    textAlign: 'left',
    padding: "37px 10px 20px 9px",
  }),
  control: (provided, state) => ({
    ...provided,
    height: "63px",
    border: "none",
    borderRadius: "5px",
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    marginTop: "0px",
    padding: "0px 5px 0px 5px",
    opacity: state.isDisabled ? 0 : 1,
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    margin: 0,
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "14px",
  }),
  menu: (provided, state) => ({
    ...provided
  }),
  menuPortal: (provided, state) => ({
    ...provided,
    zIndex: 999,
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: 0,
    height: "100%",
  })
};


export default XrInputAv;